import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  MenuItem,
  Paper,
  Radio,
  Select,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SchemaIcon from '@mui/icons-material/Schema';
import { AuthContext } from '../../context/auth-context';
import WorkflowTemplateAPI from '../../../services/api/admin/workflow-template-api';
import Logger from '../../../utils/logger';

/**
 * WorkflowTemplatePage - Super Admin page to edit the global workflow template.
 */
const WorkflowTemplatePage = () => {
  const auth = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [steps, setSteps] = useState([]);
  const [validationError, setValidationError] = useState('');

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch the global template on mount
  const fetchTemplate = async () => {
    setLoading(true);
    setValidationError('');
    try {
      Logger.debug('Fetching global workflow template', {
        hasToken: !!auth.token,
      });
      const templateData = await WorkflowTemplateAPI.getTemplate(auth.token);

      // Sort template steps by sequence_number
      const sortedSteps = [...templateData].sort(
        (a, b) => a.sequence_number - b.sequence_number
      );
      setSteps(sortedSteps);
    } catch (err) {
      Logger.error('Error fetching global workflow template', err);
      setSnackbar({
        open: true,
        message:
          err.response?.data?.message ||
          'Failed to load global workflow template.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.token) {
      fetchTemplate();
    }
  }, [auth.token]);

  // Handle name change for a step
  const handleNameChange = (index, value) => {
    setSteps((prevSteps) => {
      const updated = [...prevSteps];
      updated[index] = { ...updated[index], name: value };
      return updated;
    });
  };

  // Handle type change for a step
  const handleTypeChange = (index, value) => {
    setSteps((prevSteps) => {
      const updated = [...prevSteps];
      updated[index] = { ...updated[index], swim_lane_type: value };
      return updated;
    });
  };

  // Handle anchor change (exactly one anchor allowed)
  const handleAnchorChange = (index) => {
    setSteps((prevSteps) =>
      prevSteps.map((step, i) => ({
        ...step,
        is_field_installation_anchor: i === index,
        // Optional: clear duration if it's the anchor as the installation lane duration
        // typically comes from package installation duration, but let the user decide.
      }))
    );
  };

  // Handle default duration change
  const handleDurationChange = (index, value) => {
    setSteps((prevSteps) => {
      const updated = [...prevSteps];
      updated[index] = {
        ...updated[index],
        default_duration_days: value === '' ? null : parseInt(value, 10),
      };
      return updated;
    });
  };

  // Validate template steps before saving
  const validateTemplate = () => {
    // 1. All names must be non-empty
    const hasEmptyName = steps.some(
      (step) => !step.name || step.name.trim() === ''
    );
    if (hasEmptyName) {
      return 'All swim lane names are required and cannot be empty.';
    }

    // 2. Exactly one anchor must be selected
    const anchorCount = steps.filter(
      (step) => !!step.is_field_installation_anchor
    ).length;
    if (anchorCount !== 1) {
      return 'Exactly one swim lane must be selected as the Field Installation Anchor.';
    }

    return '';
  };

  // Save/update global template
  const handleSave = async () => {
    const errorMsg = validateTemplate();
    if (errorMsg) {
      setValidationError(errorMsg);
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      });
      return;
    }

    setValidationError('');
    setSaving(true);
    Logger.userAction('Saving global workflow template', {
      stepsCount: steps.length,
    });

    try {
      const updatedTemplate = await WorkflowTemplateAPI.saveTemplate(
        steps,
        auth.token
      );
      const sortedSteps = [...updatedTemplate].sort(
        (a, b) => a.sequence_number - b.sequence_number
      );
      setSteps(sortedSteps);

      setSnackbar({
        open: true,
        message: 'Global workflow template saved successfully!',
        severity: 'success',
      });
    } catch (err) {
      Logger.error('Error saving global workflow template', err);
      setValidationError(
        err.response?.data?.message || 'Failed to save global template.'
      );
      setSnackbar({
        open: true,
        message:
          err.response?.data?.message ||
          'Failed to save global workflow template.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Title Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <SchemaIcon color="primary" sx={{ fontSize: '2.5rem' }} />
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Workflow Template
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage the global default stages and parameters for newly created
            projects.
          </Typography>
        </Box>
      </Box>

      {/* Informational Callout */}
      <Alert severity="info" variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
        This template defines the default swim lanes applied to every new
        project. Changes here do not affect existing project workflows — only
        projects created after saving will use the updated template.
      </Alert>

      {validationError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {validationError}
        </Alert>
      )}

      {/* Main Card with Table */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell
                    sx={{ fontWeight: 'bold', width: '80px', py: 1.5 }}
                  >
                    #
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>
                    Swim Lane Name
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 'bold', width: '180px', py: 1.5 }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 'bold', width: '100px', py: 1.5 }}
                    align="center"
                  >
                    Anchor
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 'bold', width: '180px', py: 1.5 }}
                  >
                    Default Duration
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {steps.map((step, index) => (
                  <TableRow key={step.id || index} hover>
                    {/* Sequence display */}
                    <TableCell>
                      <Chip
                        label={step.sequence_number}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>

                    {/* Swim Lane Name input */}
                    <TableCell>
                      <TextField
                        value={step.name || ''}
                        onChange={(e) =>
                          handleNameChange(index, e.target.value)
                        }
                        placeholder="e.g. VDC Design"
                        fullWidth
                        size="small"
                        required
                        error={!step.name || step.name.trim() === ''}
                      />
                    </TableCell>

                    {/* Type Select */}
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={step.swim_lane_type || 'standard'}
                          onChange={(e) =>
                            handleTypeChange(index, e.target.value)
                          }
                        >
                          <MenuItem value="standard">Standard</MenuItem>
                          <MenuItem value="vdc">VDC</MenuItem>
                          <MenuItem value="prefab">Prefabrication</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* Anchor Radio */}
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Radio
                          checked={!!step.is_field_installation_anchor}
                          onChange={() => handleAnchorChange(index)}
                          size="small"
                        />
                        {step.is_field_installation_anchor && (
                          <Tooltip title="Field Installation Anchor — Reference point for all due date calculations">
                            <span
                              style={{ cursor: 'help', fontSize: '1.1rem' }}
                            >
                              ⚓
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    {/* Default Duration input */}
                    <TableCell>
                      <TextField
                        type="number"
                        value={
                          step.default_duration_days != null
                            ? step.default_duration_days
                            : ''
                        }
                        onChange={(e) =>
                          handleDurationChange(index, e.target.value)
                        }
                        placeholder={
                          step.is_field_installation_anchor ? 'Variable' : '0'
                        }
                        disabled={!!step.is_field_installation_anchor}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">days</InputAdornment>
                          ),
                          inputProps: { min: 0 },
                        }}
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Template'}
        </Button>
      </Box>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WorkflowTemplatePage;
